import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { ArtDirectionImageProps } from '@/app/components/Image/ArtDirectionImage';
import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';
import Motion from '@/app/components/Motion';
import SectionHeading from '@/app/components/SectionHeading';

import type { AccuracyCardProps } from './AccuracyCard';
import AccuracyCard from './AccuracyCard';
import MobileAccuracyCard from './MobileAccuracyCard';

export interface LandingPageAccuracyProps {
  readonly accuracyCards: AccuracyCardProps[];
  readonly backgroundImage?: ArtDirectionImageProps;
  readonly title: MessageKey;
  readonly titleShadowColor?: string;
  readonly description: MessageKey;
  readonly descriptionShadowColor?: string;
  readonly indentation?: string;
  readonly className?: string;
}

const LandingPageAccuracy = ({
  accuracyCards,
  backgroundImage,
  className,
  indentation = 'pl-40',
  title,
  titleShadowColor,
  description,
  descriptionShadowColor,
}: LandingPageAccuracyProps) => {
  const t = useTranslations();
  return (
    <div
      className={cx(
        'h-full flex-1 overflow-hidden text-sandstone-500 md:text-sandstone-200',
        className,
      )}
      data-cy="landing-page-accuracy"
    >
      <LayoutGrid className="h-full">
        {typeof backgroundImage !== 'undefined' ? (
          <GridItem
            className="relative pt-6 md:pt-0"
            colEnd={{ sm: 'main', md: 'full' }}
            colStart={{ sm: 'main', md: 'full' }}
            rowEnd={{ sm: 5, md: 3 }}
            rowStart={{ sm: 4, md: 1 }}
          >
            <div
              className="relative aspect-3/2 overflow-hidden rounded-lg md:static md:aspect-auto md:rounded-none"
              data-cy="landing-page-accuracy-bg"
            >
              <ArtDirectionImage {...backgroundImage} />
            </div>
          </GridItem>
        ) : null}
        <GridItem
          className="relative md:pt-16"
          colEnd={{ sm: 'main', md: 12, lg: 13, xl: 10 }}
          colStart={{ sm: 'main' }}
          rowEnd={{ sm: 2 }}
          rowStart={{ sm: 1 }}
        >
          <div className="relative z-10">
            <SectionHeading
              animated
              content={title}
              emAnimationProps={{
                transition: { duration: 2, type: 'tween', delay: 0.5 },
              }}
              indentation={indentation}
              multiline
              typographyProps={{
                className: 'leading-heading tracking-tighter',
                color: 'inherit',
                Element: 'h3',
                variant: 'h2',
              }}
              wrapperAnimationProps={{
                transition: { duration: 1.25, type: 'tween', delay: 0.25 },
              }}
            />
          </div>
          {titleShadowColor && (
            <div
              className="absolute z-0 hidden opacity-50 md:block"
              style={{
                top: '-50%',
                left: '-50%',
                right: '-35%',
                bottom: '-15%',
                background: `radial-gradient(50% 50% at 50% 50%, ${titleShadowColor} 60%, rgba(0, 0, 0, 0.0) 100%)`,
              }}
              data-cy="landing-page-accuracy-title-shadow"
            />
          )}
        </GridItem>
        <GridItem
          className="relative self-end pb-8 md:pb-16"
          colEnd={{ sm: 'main', md: 12, lg: 10, xl: 8 }}
          colStart={{ sm: 'main' }}
          rowEnd={{ sm: 3 }}
          rowStart={{ sm: 2 }}
        >
          <div className="relative z-20">
            <Typography color="inherit" className="pt-6 md:hidden">
              {t(description)}
            </Typography>
            <Motion
              className="hidden md:block"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1.25, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <Typography color="inherit" variant="body-large">
                {t(description)}
              </Typography>
            </Motion>
          </div>
          {descriptionShadowColor && (
            <div
              className="absolute z-0 hidden opacity-40 md:block"
              style={{
                top: '-100%',
                left: '-150%',
                right: '-60%',
                bottom: '-200%',
                background: `radial-gradient(50% 50% at 50% 50%, ${descriptionShadowColor} 50%, rgba(0, 0, 0, 0.0) 100%)`,
              }}
              data-cy="landing-page-accuracy-description-shadow"
            />
          )}
        </GridItem>
        <GridItem
          className="text-sandstone-200"
          colEnd={{ sm: 'main', md: 'main' }}
          colStart={{ sm: 'main', md: 18, lg: 18, xl: 20 }}
          rowEnd={{ sm: 4, md: 3 }}
          rowStart={{ sm: 3, md: 1 }}
        >
          <div className="flex flex-col md:hidden">
            {accuracyCards.map((card) => (
              <MobileAccuracyCard key={card.heading} {...card} />
            ))}
          </div>
          <div className="hidden h-full flex-col justify-end gap-y-6 py-16 md:flex">
            {accuracyCards.map((card) => (
              <AccuracyCard key={card.heading} {...card} />
            ))}
          </div>
        </GridItem>
      </LayoutGrid>
    </div>
  );
};

export default LandingPageAccuracy;
